import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { LogOut, LayoutDashboard, BookOpen, Settings, Sparkles, MapPin, Link2, ShieldAlert, Lock, X } from "lucide-react";
import api from "../lib/api";
import LanguageSwitcher from "./LanguageSwitcher";
import ViewAsSwitcher, { ImpersonationBanner } from "./ViewAsSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || parts[0]?.[1] || "");
}

export default function Layout({ children }) {
  const { user, logout, impersonating, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [impersonationDenied, setImpersonationDenied] = useState(false);

  // "Voir en tant que" sur un profil sans droit (VP, SG, EDA…) : le backend
  // renvoie 403 impersonated_no_access → écran "Accès refusé" au lieu d'un
  // tableau de bord vide. Le bandeau reste visible pour quitter le mode.
  useEffect(() => {
    if (!impersonating) {
      setImpersonationDenied(false);
      return;
    }
    api.get("/auth/me").catch((e) => {
      if (e?.response?.status === 403 && e?.response?.data?.detail?.code === "impersonated_no_access") {
        setImpersonationDenied(true);
      }
    });
  }, [impersonating]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/", label: t("nav.dashboard"), icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/catalogue", label: t("nav.library"), icon: BookOpen, testId: "nav-catalogue" },
  ];
  if (user?.role === "president" || user?.role === "coach" || user?.role === "ceo" || user?.role === "dn" || user?.role === "dr") {
    navItems.push({ to: "/connexion", label: t("nav.connexion"), icon: Link2, testId: "nav-connexion" });
  }
  if (user?.role === "dn" || user?.role === "dr" || user?.role === "dz" || user?.role === "ceo") {
    navItems.push({ to: "/regional", label: t("nav.regional"), icon: MapPin, testId: "nav-regional" });
  }
  if (user?.role === "ceo") {
    navItems.push({ to: "/editor", label: t("nav.editor"), icon: Settings, testId: "nav-editor" });
    navItems.push({ to: "/admin/audit", label: t("nav.audit"), icon: ShieldAlert, testId: "nav-audit" });
  }

  return (
    <div className="min-h-screen bg-white">
      <ImpersonationBanner />
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-border" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" data-testid="brand-link">
            <div className="w-9 h-9 rounded-full bg-apollo-orange flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="hidden sm:flex flex-col items-start leading-none flex-shrink-0">
              <img src="/apollo-logo.png" alt="APOLLO" className="h-4 w-auto object-contain" data-testid="brand-apollo-logo" />
              <span className="font-heading font-black text-xl tracking-tight text-apollo-orange mt-0.5 whitespace-nowrap" data-testid="brand-delphi-label">DELPHI</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-0.5 flex-shrink min-w-0 overflow-x-auto scrollbar-none" data-testid="desktop-nav">
            {navItems.map((item) => {
              const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  title={item.label}
                  className={`inline-flex items-center gap-1.5 h-9 px-2.5 lg:px-3 rounded-md text-xs lg:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    active
                      ? "bg-apollo-orange-light text-apollo-orange-dark"
                      : "text-text-secondary hover:text-text-primary hover:bg-bg-soft"
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden lg:inline">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 flex-shrink-0">
            <ViewAsSwitcher />
            <LanguageSwitcher />
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="user-menu-trigger"
                    className="inline-flex items-center gap-2 h-9 pl-1 pr-2 rounded-full border border-border bg-white hover:bg-bg-soft transition-colors"
                    aria-label={user.full_name}
                  >
                    <span className="w-7 h-7 rounded-full bg-apollo-orange text-white text-xs font-bold flex items-center justify-center uppercase">
                      {initials(user.full_name)}
                    </span>
                    <span className="hidden lg:inline text-xs font-medium text-text-primary max-w-[120px] truncate">
                      {user.full_name.split(" ")[0]}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="user-menu">
                  <DropdownMenuLabel className="pb-1">
                    <div className="text-sm font-semibold text-text-primary truncate" data-testid="user-name">{user.full_name}</div>
                    <div className="text-xs text-text-secondary truncate">{user.email}</div>
                    <div className="mt-1.5 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-apollo-orange-light text-apollo-orange-dark" data-testid="user-role">
                      {user.role_label_fr}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-600 focus:text-red-700">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t("common.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 px-4 pb-3 overflow-x-auto" data-testid="mobile-nav">
          {navItems.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-${item.testId}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                  active ? "bg-apollo-orange-light text-apollo-orange-dark" : "text-text-secondary bg-bg-soft"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
        {impersonationDenied ? (
          <div className="max-w-lg mx-auto text-center py-16" data-testid="impersonation-access-denied">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-apollo-orange-light flex items-center justify-center">
              <Lock className="w-6 h-6 text-apollo-orange-dark" />
            </div>
            <h2 className="font-heading font-bold text-xl mb-2">{t("view_as.denied_title")}</h2>
            <p className="text-text-secondary mb-6">{t("view_as.denied_body", { name: user?.full_name })}</p>
            <button
              onClick={stopImpersonating}
              data-testid="impersonation-denied-exit-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-apollo-orange text-white text-sm font-semibold hover:bg-apollo-orange-dark transition-colors"
            >
              <X className="w-4 h-4" />
              {t("view_as.exit")}
            </button>
          </div>
        ) : children}
      </main>

      <footer className="border-t border-border mt-20 py-6 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-text-secondary">
          <span data-testid="footer-tagline" className="font-oracle italic text-base">{t("common.footer")}</span>
          <span>DELPHI · APOLLO Network · 2026</span>
        </div>
      </footer>
    </div>
  );
}
